import { listen } from '@tauri-apps/api/event';

/**
 * Tauri's raw unlisten function throws an uncatchable-looking runtime error
 * ("undefined is not an object (evaluating 'listeners[eventId].handlerId')")
 * when the listener is already gone — which happens under React StrictMode's
 * dev double-mount, HMR remounts, or any double-unlisten. Every unlisten in
 * app code should go through one of these helpers instead of being called
 * raw. (workspaceFileWatcher.js predates this file and carries its own
 * equivalent guard.)
 */

/**
 * Wraps an unlisten fn so calling it twice (or after teardown) is a no-op.
 *
 * Two Tauri quirks handled here:
 * 1. `unlisten` is async — its throw surfaces as an unhandled promise
 *    rejection, so a plain try/catch around the call never sees it.
 * 2. The webview-side listener registration is eval'd by Rust *after* the
 *    `listen()` invoke can resolve, so unlistening immediately after listen
 *    resolves (the unmount-while-registering path) hits Tauri's unguarded
 *    `listeners[eventId].handlerId` read and rejects. One delayed retry runs
 *    after that eval has landed, so the listener is actually cleaned up
 *    instead of leaking; if the retry still fails there is nothing to clean.
 */
export function makeSafeUnlisten(fn) {
  let called = false;
  const attempt = () => {
    try {
      const result = fn();
      return result && typeof result.catch === 'function' ? result : Promise.resolve();
    } catch (err) {
      return Promise.reject(err);
    }
  };
  return () => {
    if (called || typeof fn !== 'function') return;
    called = true;
    attempt().catch(() => {
      setTimeout(() => {
        attempt().catch(() => {});
      }, 250);
    });
  };
}

/**
 * `await`-style drop-in for `listen()` that resolves to an idempotent,
 * throw-safe unlisten function.
 */
export async function listenSafe(event, handler) {
  const unlisten = await listen(event, handler);
  return makeSafeUnlisten(unlisten);
}

/**
 * Subscription-style listen for React effects. Returns synchronously; call
 * `.unlisten()` from the effect cleanup — it is safe to call at any time,
 * including before registration has resolved (the pending registration is
 * then unregistered as soon as it lands, closing the StrictMode
 * unmount-while-registering leak).
 *
 *   useEffect(() => {
 *     const sub = subscribeTauriEvent('my-event', onEvent);
 *     return () => sub.unlisten();
 *   }, []);
 */
export function subscribeTauriEvent(event, handler) {
  let unlistenFn = null;
  let canceled = false;

  const ready = listen(event, handler)
    .then((fn) => {
      if (canceled) {
        makeSafeUnlisten(fn)();
        return;
      }
      unlistenFn = makeSafeUnlisten(fn);
    })
    .catch((err) => {
      console.warn(`[safeTauriEvent] listen("${event}") failed:`, err);
    });

  return {
    ready,
    unlisten() {
      canceled = true;
      const fn = unlistenFn;
      unlistenFn = null;
      if (fn) fn();
    },
  };
}
